import AddIcon from '@mui/icons-material/Add';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SortIcon from '@mui/icons-material/Sort';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    Box,
    Checkbox,
    Chip,
    Collapse,
    FormControl,
    InputAdornment,
    InputLabel,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/he';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { BaseAccidentV2, UserRole } from '../../../../backendsdk';
import { ResponsiveDatePicker } from '../../../../components/MuiDatePicker';
import {
    TrackedButton as Button,
    TrackedIconButton as IconButton,
    TrackedMenuItem as MenuItem,
} from '../../../../components/TrackedComponents';
import useApi from '../../../../hooks/api';
import useIsMobile from '../../../../hooks/isMobile';
import useProfile from '../../../../hooks/profile';
import { useQuery } from '../../../../hooks/query';
import palette from '../../../ColorPalette';
import { AccidentFilters, FILTER_KEY_TO_QUERY_PARAM } from '../AccidentV1';
import { AccidentFilterField, SortField, SortOrder } from '../Defs';
import { FAULT_OPTIONS, SPEED_OPTIONS } from '../Tagging/AccidentDetails';
import { getAllChildren } from '../Tagging/TaggingTree';
import { damageCauses } from '../Tagging/treeStructures';
import { AccidentStages } from './AccidentStage';
import SortMenu from './SortMenu';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface AccidentToolbarProps {
    handlePageVersionChange: CallableFunction;
    searchText: string;
    setSearchText: Dispatch<SetStateAction<string>>;
    fromDate: Dayjs;
    setFromDate: Dispatch<SetStateAction<Dayjs>>;
    toDate: Dayjs;
    setToDate: Dispatch<SetStateAction<Dayjs>>;
    filterField: AccidentFilterField;
    setFilterField: Dispatch<SetStateAction<AccidentFilterField>>;
    resetState: CallableFunction;
    sort: [SortField, SortOrder];
    setSort: Dispatch<SetStateAction<[SortField, SortOrder]>>;
    setAlert: CallableFunction;
    stageAccidents?: Record<string, BaseAccidentV2[]>;
    currentStage?: string;
    onStageClick?: (stage: string) => void;
    filters: AccidentFilters;
    setFilters: CallableFunction;
    setAccidents: CallableFunction;
}

export const AccidentToolbar: React.FC<AccidentToolbarProps> = (props) => {
    const [anchorElSort, setAnchorElSort] = useState<null | HTMLElement>(null);
    const { t, i18n } = useTranslation();
    const ALL_FLEET = t('content.metrics.all_fleet');
    const numberOfFilterFields = Object.values(props.filters).reduce(
        (acc, curr) => acc + (!!curr.length && curr != ALL_FLEET ? 1 : 0),
        0,
    );
    const [isFiltersOpen, setIsFiltersOpen] = useState(numberOfFilterFields > 0);
    const { profile } = useProfile();
    const { agencyApi } = useApi();
    const history = useHistory();
    const isMobile = useIsMobile();
    const query = useQuery();
    const match = useRouteMatch();

    const subFleetOptions =
        profile.sub_fleets.length > 0
            ? profile.sub_fleets
            : [ALL_FLEET, ...profile.customer.sub_fleets.filter(Boolean)].sort((a, b) => a.localeCompare(b));

    const isSortOpen = Boolean(anchorElSort);
    const handleSortMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorElSort((prev) => (!prev ? event.currentTarget : null));
    };

    const handleSortMenuClose = () => {
        setAnchorElSort(null);
    };

    const downloadCSV = () => {
        agencyApi.agencyV1AccidentGet({ csv: 1 }).then(() => {
            props.setAlert({
                message: t('content.accidents.report will be sent to your email'),
                type: 'success',
                duration: 6000,
            });
        });
    };

    const handleFilterChange = (updatedFilters: AccidentFilters) => {
        props.setAccidents([]);
        for (const [key, value] of Object.entries(updatedFilters)) {
            query.delete(FILTER_KEY_TO_QUERY_PARAM[key as keyof AccidentFilters]);
            if (key === 'subFleet') {
                if (value !== ALL_FLEET) {
                    query.set('sub_fleet', value);
                }
            } else if (value.length > 0) {
                query.set(FILTER_KEY_TO_QUERY_PARAM[key as keyof AccidentFilters], value.join(','));
            }
        }
        history.push(`${match.url}?${query.toString()}`);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                flexDirection: 'column',
                alignItems: 'flex-start',
                backgroundColor: palette.neutral[50],
                boxShadow: 1,
                zIndex: 2,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px 0',
                    py: 1.5,
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, px: 1.5, flexGrow: 2 }}>
                    <TextField
                        placeholder={t('content.accidents.toolbar.search')}
                        value={props.searchText}
                        fullWidth={isMobile}
                        sx={{ width: !isMobile ? 280 : undefined, mr: 1 }}
                        InputProps={{
                            size: 'small',
                            id: 'search-field',
                            ...{ 'data-testid': 'search-field' },
                            endAdornment: !!props.searchText ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        id="btn-clear-search"
                                        onClick={() => props.setSearchText('')}
                                        edge="end"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        onChange={(e) => props.setSearchText(e.target.value)}
                    />
                    <Button
                        id="btn-sort"
                        data-testid="btn-sort"
                        variant="contained"
                        color="lightNeutral"
                        size="small"
                        startIcon={<SortIcon />}
                        sx={{ height: 40, minWidth: '75px' }}
                        onClick={handleSortMenuClick}
                    >
                        {t('content.accidents.toolbar.sort')}
                    </Button>
                    <SortMenu
                        open={isSortOpen}
                        anchorElSort={anchorElSort}
                        handleSortMenuClose={handleSortMenuClose}
                        sort={props.sort}
                        setSort={props.setSort}
                    />
                    {!isMobile ? (
                        <>
                            <Button
                                id="filter-btn"
                                variant="contained"
                                color="lightNeutral"
                                size="small"
                                startIcon={<FilterAltIcon />}
                                sx={{ height: 40 }}
                                onClick={() => setIsFiltersOpen((prev) => !prev)}
                            >
                                {`${t('content.accidents.toolbar.filter')}${
                                    !!numberOfFilterFields ? ` (${numberOfFilterFields})` : ''
                                }`}
                            </Button>
                            <Button
                                id="download-report-btn"
                                variant="contained"
                                color="lightNeutral"
                                size="small"
                                startIcon={<DownloadIcon />}
                                sx={{ height: 40 }}
                                onClick={downloadCSV}
                            >
                                {t('content.accidents.download')}
                            </Button>
                        </>
                    ) : null}
                    {!isMobile && profile.role != UserRole.CarShop ? (
                        <Button
                            id="btn-new"
                            variant="contained"
                            color="secondary"
                            size="small"
                            startIcon={<AddIcon />}
                            sx={{ height: 40 }}
                            onClick={() => history.push('/accidents/0')}
                        >
                            {t('content.accidents.toolbar.new')}
                        </Button>
                    ) : null}
                    {!isMobile ? (
                        <Box sx={{ display: 'flex' }}>
                            <Tooltip title={t('content.accidents.change_page_old')}>
                                <Box>
                                    <IconButton
                                        id="change-page-btn"
                                        onClick={() => props.handlePageVersionChange('v0')}
                                    >
                                        <SwapHorizIcon />
                                    </IconButton>
                                </Box>
                            </Tooltip>
                            <Tooltip title={t('content.accidents.refresh')}>
                                <Box>
                                    <IconButton id="refresh-btn" onClick={() => props.resetState()}>
                                        <RefreshIcon />
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        </Box>
                    ) : null}
                </Box>
                <Box sx={{ display: 'flex', px: 1.5, mt: isMobile ? 0.5 : 0 }}>
                    {!isMobile ? (
                        <FormControl sx={{ width: 175, mr: 1 }}>
                            <InputLabel id="filter-mode-label">{t('content.accidents.filters.filter_by')}</InputLabel>
                            <Select
                                id="filter-mode-field"
                                labelId="filter-mode-label"
                                fullWidth
                                size="small"
                                label={t('content.accidents.filters.filter_by')}
                                value={props.filterField}
                                onChange={(e: SelectChangeEvent) => {
                                    props.setFilterField(e.target.value as AccidentFilterField);
                                }}
                            >
                                {['timestamp', 'created_at'].map((option) => (
                                    <MenuItem key={option} value={option} id={option}>
                                        {t(`content.accidents.filters.${option}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : null}
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.languages[0]}>
                        <ResponsiveDatePicker
                            fullWidth={isMobile}
                            label={t('content.accidents.from')}
                            format={profile.dateFormat}
                            value={props.fromDate}
                            onChange={(value: Dayjs | null) => {
                                if (value !== null) {
                                    props.setFromDate(value.startOf('day'));
                                }
                            }}
                            maxDate={props.toDate}
                            slotProps={{
                                textField: {
                                    id: 'from-field',
                                    size: 'small',
                                    sx: { width: isMobile ? undefined : 175, mr: 1 },
                                },
                            }}
                        />
                        <ResponsiveDatePicker
                            fullWidth={isMobile}
                            label={t('content.accidents.to')}
                            format={profile.dateFormat}
                            value={props.toDate}
                            onChange={(value: Dayjs | null) => {
                                if (value !== null) {
                                    props.setToDate(value.endOf('day'));
                                }
                            }}
                            maxDate={dayjs()}
                            slotProps={{
                                textField: { id: 'to-field', size: 'small', sx: { width: isMobile ? undefined : 175 } },
                            }}
                        />
                    </LocalizationProvider>
                </Box>
                {isMobile && !!props.stageAccidents && !!props.currentStage && !!props.onStageClick ? (
                    <AccidentStages
                        stageAccidents={props.stageAccidents}
                        currentStage={props.currentStage}
                        onStageClick={props.onStageClick}
                    />
                ) : null}
            </Box>
            <Collapse orientation="vertical" in={isFiltersOpen}>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.5,
                        pb: 1.5,
                        gap: '12px 8px',
                        flexWrap: 'wrap',
                    }}
                >
                    <FormControl size="small" sx={{ width: '150px' }}>
                        <InputLabel>{t('content.metrics.sub_fleet')}</InputLabel>
                        <Select
                            id="sub-fleet-filter-select"
                            data-testid="sub-fleet-filter-select"
                            label={t('content.metrics.sub_fleet')}
                            variant="outlined"
                            size="small"
                            value={props.filters.subFleet}
                            onChange={(e) => {
                                props.setFilters((prev: AccidentFilters) => {
                                    const updatedFilters = { ...prev, subFleet: e.target.value };
                                    handleFilterChange(updatedFilters);
                                    return updatedFilters;
                                });
                            }}
                        >
                            {subFleetOptions.map((subFleet) => (
                                <MenuItem key={subFleet} value={subFleet} id={subFleet}>
                                    {subFleet}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="accident-cause-filter-field"
                        size="small"
                        options={getAllChildren(damageCauses).sort((a, b) =>
                            t(`content.accidents.tags.damage_cause.${a}`).localeCompare(
                                t(`content.accidents.tags.damage_cause.${b}`),
                            ),
                        )}
                        value={props.filters.damageCause}
                        onChange={(_, newValue) => {
                            props.setFilters((prev: AccidentFilters) => {
                                const updatedFilters = { ...prev, damageCause: newValue || [] };
                                handleFilterChange(updatedFilters);
                                return updatedFilters;
                            });
                        }}
                        sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('content.accidents.tags.damage_cause.header')}
                                sx={{ width: 350 }}
                            />
                        )}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} style={{ height: 40, paddingLeft: 0 }}>
                                <Checkbox
                                    id={`${option}-checkbox`}
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {t(`content.accidents.tags.damage_cause.${option}`)}
                            </li>
                        )}
                        renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                            return (
                                <>
                                    <Chip
                                        size="small"
                                        label={
                                            <Typography
                                                fontSize={12}
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            >
                                                {t(`content.accidents.tags.damage_cause.${value[0]}`)}
                                            </Typography>
                                        }
                                        {...getTagProps({ index: 0 })}
                                        sx={{ borderRadius: 1, maxWidth: '250px !important' }}
                                        onDelete={undefined}
                                    />
                                    {value.length > 1 ? (
                                        <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                                    ) : null}
                                </>
                            );
                        }}
                    />
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="speed-filter-field"
                        size="small"
                        options={SPEED_OPTIONS}
                        value={props.filters.speed}
                        onChange={(_, newValue) => {
                            props.setFilters((prev: AccidentFilters) => {
                                const updatedFilters = { ...prev, speed: newValue || [] };
                                handleFilterChange(updatedFilters);
                                return updatedFilters;
                            });
                        }}
                        sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('content.accidents.tags.accident_details.speed.header')}
                                sx={{ width: 225 }}
                            />
                        )}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} style={{ height: 40, paddingLeft: 0 }}>
                                <Checkbox
                                    id={`${option}-checkbox`}
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {t(
                                    `content.accidents.tags.accident_details.speed.${
                                        profile.locale === 'il' ? 'kmh' : 'mph'
                                    }.${option}`,
                                )}
                            </li>
                        )}
                        renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                            return (
                                <>
                                    <Chip
                                        size="small"
                                        label={
                                            <Typography
                                                fontSize={12}
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            >
                                                {t(
                                                    `content.accidents.tags.accident_details.speed.${
                                                        profile.locale === 'il' ? 'kmh' : 'mph'
                                                    }.${value[0]}`,
                                                )}
                                            </Typography>
                                        }
                                        {...getTagProps({ index: 0 })}
                                        sx={{ borderRadius: 1, maxWidth: '150px !important' }}
                                        onDelete={undefined}
                                    />
                                    {value.length > 1 ? (
                                        <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                                    ) : null}
                                </>
                            );
                        }}
                    />
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="fault-filter-field"
                        size="small"
                        options={FAULT_OPTIONS}
                        value={props.filters.fault}
                        onChange={(_, newValue) => {
                            props.setFilters((prev: AccidentFilters) => {
                                const updatedFilters = { ...prev, fault: newValue || [] };
                                handleFilterChange(updatedFilters);
                                return updatedFilters;
                            });
                        }}
                        sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('content.accidents.tags.accident_details.fault.header')}
                                sx={{ width: 225 }}
                            />
                        )}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} style={{ height: 40, paddingLeft: 0 }}>
                                <Checkbox
                                    id={`${option}-checkbox`}
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {t(`content.accidents.tags.accident_details.fault.${option}`)}
                            </li>
                        )}
                        renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                            return (
                                <>
                                    <Chip
                                        size="small"
                                        label={
                                            <Typography
                                                fontSize={12}
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            >
                                                {t(`content.accidents.tags.accident_details.fault.${value[0]}`)}
                                            </Typography>
                                        }
                                        {...getTagProps({ index: 0 })}
                                        sx={{ borderRadius: 1, maxWidth: '150px !important' }}
                                        onDelete={undefined}
                                    />
                                    {value.length > 1 ? (
                                        <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                                    ) : null}
                                </>
                            );
                        }}
                    />
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="point-of-impact-filter-field"
                        size="small"
                        options={[
                            'front',
                            'front_right',
                            'right',
                            'rear_right',
                            'rear',
                            'rear_left',
                            'left',
                            'front_left',
                            'roof',
                            'undercarriage',
                        ]}
                        value={props.filters.pointOfImpact}
                        onChange={(_, newValue) => {
                            props.setFilters((prev: AccidentFilters) => {
                                const updatedFilters = { ...prev, pointOfImpact: newValue || [] };
                                handleFilterChange(updatedFilters);
                                return updatedFilters;
                            });
                        }}
                        sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('content.accidents.tags.damage_mechanism.point_of_impact')}
                                sx={{ width: 225 }}
                            />
                        )}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} style={{ height: 40, paddingLeft: 0 }}>
                                <Checkbox
                                    id={`${option}-checkbox`}
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {t(`content.accidents.tags.damage_mechanism.${option}`)}
                            </li>
                        )}
                        renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                            return (
                                <>
                                    <Chip
                                        size="small"
                                        label={
                                            <Typography
                                                fontSize={12}
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            >
                                                {t(`content.accidents.tags.damage_mechanism.${value[0]}`)}
                                            </Typography>
                                        }
                                        {...getTagProps({ index: 0 })}
                                        sx={{ borderRadius: 1, maxWidth: '150px !important' }}
                                        onDelete={undefined}
                                    />
                                    {value.length > 1 ? (
                                        <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                                    ) : null}
                                </>
                            );
                        }}
                    />
                    <Button
                        id="btn-clear-filters"
                        variant="contained"
                        color="lightNeutral"
                        size="small"
                        disabled={numberOfFilterFields === 0}
                        onClick={() =>
                            props.setFilters({
                                subFleet: ALL_FLEET,
                                damageCause: [],
                                speed: [],
                                fault: [],
                                pointOfImpact: [],
                            })
                        }
                        sx={{ height: '40px' }}
                    >
                        {t('content.accidents.filters.clear')}
                    </Button>
                </Box>
            </Collapse>
        </Box>
    );
};
